import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Calendar } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useLang, useLocalePath } from "../contexts/LanguageContext";
import SEO, { organizationSchema, articleSchema, BASE_URL } from "../components/SEO";
import { Helmet } from "react-helmet-async";

export default function BlogDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const localePath = useLocalePath();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/blog/${slug}`); setPost(data); }
      catch { setErr("Post not found."); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-ink/60">{t("common.loading")}</div>;
  if (err) return <div className="py-32 text-center text-ink/60">{err}</div>;

  const canonical = `${BASE_URL}/${lang}/blog/${slug}`;

  return (
    <article>
      {/* Article-specific meta (overrides SEO component for dynamic content) */}
      <Helmet>
        <html lang={lang} />
        <title>{`${post.title} | Loka Samabaya Pratisthan`}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={post.cover_image ? `${BASE_URL}${post.cover_image}` : `${BASE_URL}/images/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema({
            title: post.title, description: post.excerpt, image: post.cover_image,
            datePublished: post.created_at, slug, lang,
          }))}
        </script>
      </Helmet>

      <section className="paper-texture">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <Link to={localePath("/blog")} data-testid="blog-back-link" className="inline-flex items-center gap-2 text-clay hover:text-forest text-[14px] mb-8">
            <ArrowLeft className="w-4 h-4" /> {t("common.all_articles")}
          </Link>
          <Reveal>
            <div className="flex items-center gap-4 text-[12px] tracking-[0.25em] uppercase text-clay mb-5">
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(post.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              {post.tags?.map((tg) => <span key={tg}>· {tg}</span>)}
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-forest">{post.title}</h1>
            <p className="mt-6 text-lg text-ink/75 leading-relaxed italic">{post.excerpt}</p>
          </Reveal>
        </div>
      </section>

      {post.cover_image && (
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <img src={post.cover_image} alt={post.title} className="w-full h-[60vh] object-cover rounded-xl" />
        </div>
      )}

      <section className="bg-ivory">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
          <Reveal>
            <div>
              {post.content.split("\n\n").map((para, i) => (
                <p key={i} className="mb-6 whitespace-pre-wrap text-[17px] leading-[1.75] text-ink/80">{para}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
